import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AddProductPage.module.scss';
import { CourseInfoForm } from '../../../components/CourseInfoForm/CourseInfoForm';
import { ImagesBlock } from "../../../components/ImagesBlock/ImagesBlock";
import { Button } from "../../../components/Button/Button";
import { CourseContentSection, VideoItem } from '../../../components/CourseContentSection/CourseContentSection';
import { ProductPreview } from '../../../components/ProductPreview/ProductPreview';
import { LoadingIndicator } from '../../../components/LoadingIndicator/LoadingIndicator';
import { ReactComponent as BackButtonIcon } from '../../../assets/icons/back-button-icon.svg';
import { ReactComponent as CloseBackButton } from '../../../assets/icons/close-back-button.svg';
import { addProduct } from '../../../api/productApi'

type Step = 1 | 2 | 3;

export function AddProductPage() {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState<Step>(1);
    const [isLoading, setIsLoading] = useState(false);

    const [content, setContent] = React.useState<{
        description: string;
        videos: VideoItem[];
    }>({
        description: '',
        videos: [{ file: null, previewUrl: null, title: "", isDemo: false }],
    });

    const [courseData, setCourseData] = useState({
        title: '',
        author: '',
        price: '',
        discount: ''
    });

    const [images, setImages] = useState<(File | null)[]>(Array(9).fill(null));

    const handleImageChange = (file: File, index: number) => {
        const newImages = [...images];
        newImages[index] = file;
        setImages(newImages);
    };

    const handleRemove = (index: number) => {
        const newImages = [...images];
        newImages[index] = null;
        setImages(newImages);
    };

    const goBack = () => navigate('/admin/dashboard/', { replace: true });

    const handleCourseChange = (field: string, value: string) => {
        setCourseData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setIsLoading(true);
        const formData = new FormData();
        formData.append("title", courseData.title);
        formData.append("author", courseData.author);
        formData.append("price", courseData.price);
        formData.append("discount", courseData.discount);
        formData.append("description", content.description);

        images.forEach((file, index) => {
            if (file) {
                formData.append(`images[${index}]`, file);
            }
        });

        content.videos.forEach((v, idx) => {
            formData.append(`videos[${idx}][title]`, v.title);
            formData.append(`videos[${idx}][isDemo]`, String(v.isDemo));
            if (v.file) {
                formData.append(`videos[${idx}][file]`, v.file);
            }
        });

        try {
            await addProduct(formData);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
            navigate('/admin/dashboard/');
        }
    };

    const isProductValid = () => {
        const hasImages = images.some(Boolean);
        const allVideosHaveTitle = content.videos.every(v => v.title.trim() !== '');
        const price = parseFloat(courseData.price) || 0;
        const discount = parseFloat(courseData.discount) || 0;
        const hasDiscountError = discount > price;

        return (
            courseData.title.trim() !== '' &&
            courseData.author.trim() !== '' &&
            courseData.price.trim() !== '' &&
            hasImages &&
            content.description.trim() !== '' &&
            allVideosHaveTitle &&
            !hasDiscountError
        );
    };


    return (
        <div className={styles.page}>
            {isLoading && <LoadingIndicator message="Сохранение продукта..." />}
            <div className={styles.backRow}>
                <button className={styles.backBtn} onClick={goBack} aria-label="Назад">
                    <BackButtonIcon />
                </button>
            </div>
            <div className={styles.titleRow}>
                <h1 className={styles.pageTitle}>Создание товара</h1>
                <button className={styles.closeBtn} onClick={goBack} aria-label="Закрыть и вернуться">
                    <CloseBackButton />
                </button>
            </div>
            <div className={styles.stepsWrapper}>
                <div className={styles.topBar}>
                    {[1, 2, 3].map(step => (
                        <button
                            key={step}
                            type="button"
                            className={`${styles.stepCard} ${styles[`step${step}`]} ${activeStep === step ? styles.active : ''}`}
                            onClick={() => setActiveStep(step as Step)}
                        >
                            <span className={`${styles.phaseCircle} ${activeStep === step ? styles.phaseActive : ''}`}>{step}</span>
                            <span className={styles.stepLabel}>
                                {step === 1 ? 'Информация о товаре' :
                                    step === 2 ? 'Содержание курса' : 'Предварительный просмотр'}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
            <div className={styles.headerLine}></div>
            <div className={styles.contentArea}>
                {activeStep === 1 && (
                    <>
                        <CourseInfoForm data={courseData} onChange={handleCourseChange} />
                        <ImagesBlock images={images} onChange={handleImageChange} onRemove={handleRemove} />
                        <div className={styles.headerLine}></div>
                        <div className={styles.buttonsRow}>
                            <Button label="Отменить" variant="cancel" onClick={() => navigate("/admin/dashboard")} />
                            <Button label="К следующему шагу" variant="next" onClick={() => setActiveStep(2 as Step)} />
                        </div>
                    </>
                )}
                {activeStep === 2 && (
                    <>
                        <CourseContentSection
                            description={content.description}
                            videos={content.videos}
                            onDescriptionChange={(val) => setContent((p) => ({ ...p, description: val }))}
                            onVideosChange={(items) => setContent((p) => ({ ...p, videos: items }))}
                        />
                        <div className={styles.headerLine}></div>
                        <div className={styles.buttonsRow}>
                            <Button label="Назад" variant="cancel" onClick={() => setActiveStep(1 as Step)} />
                            <Button label="К следующему шагу" variant="next" onClick={() => setActiveStep(3 as Step)} />
                        </div>
                    </>
                )}
                {activeStep === 3 && (
                    <div className={styles.card}>
                        <ProductPreview
                            title={courseData.title}
                            author={courseData.author}
                            price={courseData.price}
                            discount={courseData.discount}
                            images={images.map(f => f ? URL.createObjectURL(f) : null)}
                            description={content.description}
                            videos={content.videos.map(v => ({
                                title: v.title,
                                isDemo: v.isDemo,
                                fileUrl: v.file ? URL.createObjectURL(v.file) : null
                            }))}
                        />

                        <div className={styles.headerLine}></div>
                        <div className={styles.buttonsRow}>
                            <Button label="Назад" variant="cancel" onClick={() => setActiveStep(2 as Step)} />
                            <Button
                                label="Сохранить продукт"
                                variant="next"
                                onClick={handleSave}
                                disabled={!isProductValid() || isLoading}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}